import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { supportService } from '../api/services/support.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { ListPagination } from '../components/ui/list-pagination'
import { useAuth } from '../context/AuthContext'
import { useSupportRealtime } from '../hooks/useSupportRealtime'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { cn } from '../lib/utils'
import type { SupportRequest } from '../types/support'

const replySchema = z.object({
  body: z.string().min(1, 'Reply is required'),
})

type ReplyFormValues = z.infer<typeof replySchema>

function statusBadgeClass(status: SupportRequest['status']) {
  if (status === 'closed') {
    return 'bg-parch-100 text-ink-600'
  }

  if (status === 'answered') {
    return 'bg-green-50 text-green-800'
  }

  return 'bg-warn-lt text-warn'
}

export function AdminSupportPage() {
  const { can } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const ticketFromQuery = searchParams.get('ticket')
  const [tickets, setTickets] = useState<SupportRequest[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedTicket, setSelectedTicket] = useState<SupportRequest | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [closing, setClosing] = useState(false)
  const [replying, setReplying] = useState(false)

  const replyForm = useForm<ReplyFormValues>({
    resolver: zodResolver(replySchema),
    defaultValues: { body: '' },
  })

  const loadTickets = useCallback(async () => {
    try {
      const response = await supportService.list({ page, per_page: perPage })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setTickets(items)
      setMeta(nextMeta)
    } catch {
      setTickets([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [page, perPage])

  const loadDetail = useCallback(async (id: string) => {
    setLoadingDetail(true)

    try {
      const response = await supportService.show(id)
      setSelectedTicket(response.data)
    } catch {
      setSelectedTicket(null)
      setSelectedId(null)
    } finally {
      setLoadingDetail(false)
    }
  }, [])

  const upsertTicketInList = useCallback((ticket: SupportRequest) => {
    setTickets((current) => {
      const listItem: SupportRequest = {
        id: ticket.id,
        user_id: ticket.user_id,
        subject: ticket.subject,
        body: ticket.body,
        status: ticket.status,
        user: ticket.user,
        created_at: ticket.created_at,
        updated_at: ticket.updated_at,
      }
      const exists = current.some((item) => item.id === ticket.id)

      if (!exists) {
        return [listItem, ...current]
      }

      return current.map((item) => (item.id === ticket.id ? { ...item, ...listItem } : item))
    })
  }, [])

  const refreshTicketFromRealtime = useCallback(
    async (supportRequestId: string) => {
      try {
        const response = await supportService.show(supportRequestId)
        const ticket = response.data
        upsertTicketInList(ticket)

        setSelectedId((currentId) => {
          if (currentId === supportRequestId) {
            setSelectedTicket(ticket)
          }

          return currentId
        })
      } catch {
        // Bell/toast already surfaced the event.
      }
    },
    [upsertTicketInList],
  )

  useSupportRealtime({
    onTicketCreated: (supportRequestId) => {
      if (page !== 1) {
        return
      }

      void refreshTicketFromRealtime(supportRequestId)
    },
    onTicketReply: (supportRequestId) => {
      void refreshTicketFromRealtime(supportRequestId)
    },
  })

  useEffect(() => {
    void loadTickets()
  }, [loadTickets])

  useEffect(() => {
    if (!ticketFromQuery || !hasLoaded) {
      return
    }

    setSelectedId(ticketFromQuery)

    setSearchParams(
      (prev) => {
        if (!prev.has('ticket')) {
          return prev
        }

        const next = new URLSearchParams(prev)
        next.delete('ticket')
        return next
      },
      { replace: true },
    )
  }, [hasLoaded, setSearchParams, ticketFromQuery])

  useEffect(() => {
    if (selectedId) {
      replyForm.reset({ body: '' })
      void loadDetail(selectedId)
    } else {
      setSelectedTicket(null)
    }
  }, [loadDetail, replyForm, selectedId])

  const onReply = async (values: ReplyFormValues) => {
    if (!selectedId) {
      return
    }

    setReplying(true)

    try {
      await supportService.reply(selectedId, values)
      replyForm.reset({ body: '' })
      await loadTickets()
      await loadDetail(selectedId)
    } catch {
      // handled globally
    } finally {
      setReplying(false)
    }
  }

  const handleClose = async () => {
    if (!selectedId) {
      return
    }

    setClosing(true)

    try {
      await supportService.close(selectedId)
      await loadTickets()
      await loadDetail(selectedId)
    } catch {
      // handled globally
    } finally {
      setClosing(false)
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold">Support requests</h1>
          <p className="mt-1 text-sm text-ink-600">
            Review and respond to user support tickets.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card lg:col-span-2">
            <div className="border-b border-parch-100 bg-parch-50 px-4 py-3">
              <p className="text-sm font-medium text-ink-700">All tickets</p>
            </div>

            {hasLoaded && tickets.length === 0 ? (
              <p className="px-4 py-6 text-sm text-ink-500">No support tickets.</p>
            ) : (
              <ul>
                {tickets.map((ticket) => (
                  <li key={ticket.id} className="border-b border-parch-50 last:border-0">
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-parch-50/60',
                        selectedId === ticket.id && 'bg-green-50/60',
                      )}
                      onClick={() => setSelectedId(ticket.id)}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink-900">
                          {ticket.subject}
                        </p>
                        <p className="mt-1 truncate text-xs text-ink-500">
                          {ticket.user?.name ?? 'Unknown user'}
                          {ticket.user?.email ? ` · ${ticket.user.email}` : ''}
                        </p>
                      </div>
                      <span
                        className={cn(
                          'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize',
                          statusBadgeClass(ticket.status),
                        )}
                      >
                        {ticket.status}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {meta && meta.total > 0 && (
              <div className="border-t border-parch-100 px-4 py-3">
                <ListPagination
                  meta={meta}
                  perPage={perPage}
                  onPageChange={setPage}
                  onPerPageChange={(next) => {
                    setPerPage(next)
                    setPage(1)
                  }}
                />
              </div>
            )}
          </section>

          <section className="rounded-lg border border-parch-100 bg-white p-6 shadow-card lg:col-span-3">
            {!selectedId ? (
              <p className="text-sm text-ink-500">Select a ticket to view details.</p>
            ) : loadingDetail ? (
              <p className="text-sm text-ink-500">Loading…</p>
            ) : !selectedTicket ? (
              <p className="text-sm text-ink-500">Ticket not found.</p>
            ) : (
              <div>
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-display text-lg font-bold text-ink-900">
                        {selectedTicket.subject}
                      </h2>
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-1 text-xs font-medium capitalize',
                          statusBadgeClass(selectedTicket.status),
                        )}
                      >
                        {selectedTicket.status}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-ink-500">
                      {selectedTicket.user?.name ?? 'Unknown user'}
                      {selectedTicket.user?.email ? ` · ${selectedTicket.user.email}` : ''}
                    </p>
                  </div>
                  {can('support-requests-close') && selectedTicket.status !== 'closed' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={closing}
                      onClick={() => void handleClose()}
                    >
                      {closing ? 'Closing…' : 'Close Ticket'}
                    </Button>
                  )}
                </div>

                <div className="rounded-md border border-parch-100 bg-parch-50/60 p-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
                    Original message
                  </p>
                  <p className="whitespace-pre-wrap text-sm text-ink-800">{selectedTicket.body}</p>
                </div>

                {selectedTicket.replies && selectedTicket.replies.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">
                      Replies
                    </p>
                    {selectedTicket.replies.map((reply) => (
                      <div
                        key={reply.id}
                        className="rounded-md border border-parch-100 bg-white p-3 text-sm"
                      >
                        <p className="mb-1 text-xs font-medium text-ink-500">
                          {reply.user?.name ?? 'Staff'} ·{' '}
                          {reply.created_at ? new Date(reply.created_at).toLocaleString() : '—'}
                        </p>
                        <p className="whitespace-pre-wrap text-ink-800">{reply.body}</p>
                      </div>
                    ))}
                  </div>
                )}

                <PermissionGate permission="support-requests-reply">
                  {selectedTicket.status !== 'closed' && (
                    <form
                      className="mt-6 space-y-3 border-t border-parch-100 pt-4"
                      onSubmit={replyForm.handleSubmit(onReply)}
                    >
                      <FormRequiredLegend />
                      <div>
                        <FieldLabel required>Reply</FieldLabel>
                        <textarea
                          className="min-h-28 w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
                          {...replyForm.register('body')}
                        />
                        {replyForm.formState.errors.body && (
                          <p className="mt-1 text-xs text-danger">
                            {replyForm.formState.errors.body.message}
                          </p>
                        )}
                      </div>
                      <Button type="submit" disabled={replying}>
                        {replying ? 'Sending…' : 'Send Reply'}
                      </Button>
                    </form>
                  )}
                </PermissionGate>
              </div>
            )}
          </section>
        </div>
      </div>
    </AppShell>
  )
}
